import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Col, Container, Row } from 'react-bootstrap';

import AppLayout from '../../../layouts/AppLayout/AppLayout';
import { useLanguage } from '../../../context/LanguageProvider/LanguageProvider';
import { getSolutionPage, SOLUTION_PAGE_IDS } from '../../../data/solutionPages';
import { NioButton, NioIcon } from '../../../components';
import ExpertiseImage from '../../../components/ExpertiseImage/ExpertiseImage';
import ExpertiseTechTags from '../../../components/ExpertiseTechTags/ExpertiseTechTags';

function SolutionPage({ pageId }) {
  const { t } = useLanguage();
  const page = getSolutionPage(pageId);

  if (!page) {
    return <Navigate to="/404" replace />;
  }

  const imageItem = { image: page.image, imageWebp: page.imageWebp };

  return (
    <AppLayout variant={2} titleKey={page.titleKey} rootClass="layout-1">
      <div className="jassp-solution-page">
        <section className="jassp-expertises-hero pt-7 pt-lg-120 pb-5 pb-lg-6">
          <Container>
            <p className="mb-4">
              <Link to="/" className="jassp-expertise-detail-back link-light text-decoration-none">
                <NioIcon name="arrow-left" className="me-2" />
                {t('pages.solutions.backHome')}
              </Link>
            </p>
            <Row className="justify-content-center">
              <Col lg={10} xl={9}>
                <div className="d-flex align-items-start gap-3 mb-3">
                  <span className="jassp-expertise-detail-hero-icon" aria-hidden="true">
                    <NioIcon name={page.icon} />
                  </span>
                  <h1 className="jassp-expertises-hero-title display-5 fw-bold mb-0">{t(page.titleKey)}</h1>
                </div>
                <p className="jassp-expertises-hero-lead fs-18 mb-4">{t(page.leadKey)}</p>
                <ExpertiseTechTags keywordKeys={page.techKeywordsKeys} />
              </Col>
            </Row>
          </Container>
        </section>

        <section className="py-6 py-lg-7">
          <Container>
            <Row className="align-items-start gy-5">
              <Col lg={5} className="text-center text-lg-start">
                <div className="jassp-expertise-detail-photo-wrap jassp-expertise-detail-photo-wrap--inline">
                  <ExpertiseImage item={imageItem} alt={t(page.altKey)} variant="inline" />
                </div>
              </Col>
              <Col lg={7}>
                <div className="jassp-expertise-detail-content">
                  <p className="fs-18 text-gray-800 mb-4">{t(page.bodyKey)}</p>
                  {page.highlightsKeys?.length > 0 && (
                    <>
                      <h2 className="h5 fw-bold mb-3">{t('expertises.detail.highlightsTitle')}</h2>
                      <ul className="jassp-expertise-detail-highlights ps-3 mb-5">
                        {page.highlightsKeys.map((key) => (
                          <li key={key} className="fs-16 text-gray-700 mb-2">
                            {t(key)}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                  <div className="d-flex flex-wrap gap-2">
                    {page.expertiseSlug ? (
                      <Link
                        to={`/expertises/${page.expertiseSlug}`}
                        className="btn btn-outline-primary"
                      >
                        {t('pages.solutions.viewExpertise')}
                        <NioIcon name="arrow-right" className="ms-2" />
                      </Link>
                    ) : null}
                    {page.realisationId ? (
                      <Link
                        to="/realisations"
                        className="btn btn-outline-secondary"
                      >
                        {t('pages.solutions.viewProject')}
                      </Link>
                    ) : null}
                    {page.externalUrl ? (
                      <a
                        href={page.externalUrl}
                        className="btn btn-outline-secondary"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t('pages.solutions.visitPlatform')}
                        <NioIcon name="arrow-right" className="ms-2" />
                      </a>
                    ) : null}
                    <NioButton as="link" to="/contact-us" label={t('expertises.methodology.cta')} className="btn-primary">
                      <NioIcon name="arrow-right" className="ms-2" />
                    </NioButton>
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        <section className="jassp-expertise-block--alt py-6 py-lg-7">
          <Container>
            <h2 className="h4 fw-bold mb-4">{t('pages.solutions.otherSolutions')}</h2>
            <div className="jassp-expertises-icon-grid jassp-expertises-icon-grid--light">
              {SOLUTION_PAGE_IDS.filter((id) => id !== pageId).map((id) => {
                const entry = getSolutionPage(id);
                return (
                  <Link key={id} to={`/${id}`} className="jassp-expertises-icon-card">
                    <span className="jassp-expertises-icon-wrap" aria-hidden="true">
                      <NioIcon name={entry.icon} />
                    </span>
                    <span className="jassp-expertises-icon-label">{t(entry.titleKey)}</span>
                  </Link>
                );
              })}
            </div>
          </Container>
        </section>
      </div>
    </AppLayout>
  );
}

export default SolutionPage;
